/**
 * Query String Utilities
 * Pure functions for working with URL query parameters
 */

/**
 * Convert params object to URLSearchParams
 * Accepts any Record with string/number/boolean/Array values
 *
 * @param params - Object with query parameters
 * @returns URLSearchParams instance
 * @example
 * toURLSearchParams({ page: 1, filters: ['a', 'b'] })
 * // => URLSearchParams with "page=1&filters=a&filters=b"
 */
export function toURLSearchParams(params: Record<string, string | number | boolean | Array<string> | undefined>): URLSearchParams {
    const searchParams = new URLSearchParams();

    for (const [key, value] of Object.entries(params)) {
        if (value === undefined || value === null) {
            continue;
        }

        if (Array.isArray(value)) {
            for (const item of value) {
                searchParams.append(key, String(item));
            }
        } else {
            searchParams.append(key, String(value));
        }
    }

    return searchParams;
}

/**
 * Converts query parameters object to URL search string
 *
 * @param params - Object with query parameters
 * @returns Query string with leading "?" or empty string
 * @example
 * buildQueryString({ page: 1, search: 'test' })
 * // => "?page=1&search=test"
 */
export function buildQueryString(params: Record<string, string | number | boolean | Array<string> | undefined>): string {
    const searchParams = toURLSearchParams(params);
    const queryString = searchParams.toString();
    return queryString ? `?${queryString}` : "";
}

/**
 * Merges destination params with current user filters, giving priority to current filters
 *
 * This function takes destination default params (e.g., from a location API response)
 * and merges them with the current user-selected filters. If a parameter exists in both,
 * the user's filter value takes precedence.
 *
 * @param destinationParams
 * @param currentFilters
 * @returns Merged query string with user filters overriding destination defaults
 *
 */
export function mergeQueryParams(destinationParams: string, currentFilters: string): string {
    if (!destinationParams || destinationParams === "") {
        return currentFilters;
    }

    if (!currentFilters || currentFilters === "") {
        return destinationParams;
    }

    const destParams = new URLSearchParams(destinationParams.replace(/^\?/, ""));
    const currentParams = new URLSearchParams(currentFilters);

    const merged = new URLSearchParams();

    for (const [key, value] of destParams.entries()) {
        merged.set(key, value);
    }

    for (const [key, value] of currentParams.entries()) {
        merged.set(key, value);
    }

    return merged.toString();
}
